import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { NgClass, UpperCasePipe } from '@angular/common';
import { CompoundFilterSelectionComponent } from '../../../compound-filter-selection/compound-filter-selection.component';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Filter, IOption } from '../../filters';
import { getDatesBySelectedOption, DateRangeFilterOptions } from './filter-option.const';
import {
  FILTER_CONFIG_TOKEN,
  DEFAULT_OPT,
  DefaultFilterOptionConfigFactory,
  EXCLUDE_EMPTY,
  EXCLUDE_SELECTION,
  FILTER_OPTIONS_CONFIG_TOKEN,
  FilterOptionMenuItem,
  DateRangeFilterOption,
  INCLUDE_EMPTY,
} from './filter-options-config.service';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { FILTER_OPTION_SERVICE_TOKEN, NoopFilterOptionService } from './filter-option-handler';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CompoundFilterConfig } from '../../../compound-filter-selection/compound-filter-selection.model';

@UntilDestroy()
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[cbre-filter-option]',
  imports: [
    NgClass,
    UpperCasePipe,
    MatMenuModule,
    MatIconModule,
    MatButtonToggleModule,
    ReactiveFormsModule,
    CompoundFilterSelectionComponent,
  ],
  templateUrl: './filter-option.component.html',
  styleUrls: ['./filter-option.component.scss'],
})
export class FilterOptionComponent implements OnInit {
  private readonly filterOptionService =
    inject(FILTER_OPTION_SERVICE_TOKEN, { optional: true }) ?? NoopFilterOptionService;
  private readonly filterOptions =
    inject(FILTER_OPTIONS_CONFIG_TOKEN, { optional: true }) ?? DefaultFilterOptionConfigFactory();
  private readonly filterConfig = inject(FILTER_CONFIG_TOKEN, { optional: true });

  form = input.required<UntypedFormGroup>();
  filter = input.required<Filter>();

  filterOptionChange = output<Filter>();

  compoundFilterConfig = signal<CompoundFilterConfig | null>(null);
  currentCompoundFilterKeys = signal<number[] | undefined>(undefined);
  pendingCompoundFilterKeys: number[] | undefined;
  filterGroups = computed(() => this.compoundFilterConfig()?.groups ?? []);
  showOptionMenuIcon = computed(() => !this.filter()?.hideOptionMenuIcon);
  defaultSelectedKeys = computed(() => {
    const config = this.compoundFilterConfig();
    if (!config) return [];
    return config.defaultSelectedKeys?.length
      ? config.defaultSelectedKeys
      : (config?.groups?.flatMap(g => g.items.map(i => i.key)) ?? []);
  });

  isDateRangeFilter = computed(() => {
    const { type, dataType, inputType } = this.filter();
    return type === 'range' && (dataType === 'date' || inputType === 'date');
  });

  isReports = input<boolean>(false);
  includeEmpty = false;
  excludeEmpty = false;
  excludeSelection = false;

  dateFilterOptions: DateRangeFilterOption[] = [];
  filterOptionControl = new UntypedFormControl();
  disableDateFilter = false;
  private readonly optionMenuIcons = {
    default: 'filter-option',
    selected: 'filter-option-green',
  } as const;
  optionMenuIcon: string = this.optionMenuIcons.default;
  menuOptions: FilterOptionMenuItem[] = [];

  hasCompoundFilters = computed(() => {
    const filter = this.filter();
    return filter.value !== undefined && this.hasCompoundFilterSupport(filter);
  });

  ngOnInit(): void {
    this.setFilterOptionControl();
    this.initDateFilterOptions();
    this.filterOptionService.clearNullFilter$.pipe(untilDestroyed(this)).subscribe(() => {
      this.filterOptionControl.setValue('default');
    });
    this.disableFormOnExcludeEmpty();

    const filter = this.filter();
    this.menuOptions = this.filterOptions.getFilterOptionsMenu({
      isReports: this.isReports(),
      filter: filter as any,
    });

    this.initCompoundFilterState();
  }

  private initDateFilterOptions() {
    if (this.isDateRangeFilter() && this.filterConfig?.dateRange) {
      this.dateFilterOptions = this.isReports()
        ? this.filterConfig.dateRange.getOptionsForReports()
        : this.filterConfig.dateRange.getOptionsForRecordSearch();
    }
  }

  onCompoundFilterSelectionChange(newKeys: number[]): void {
    this.pendingCompoundFilterKeys = newKeys;
    this.currentCompoundFilterKeys.set([...newKeys]);
    this.optionMenuIcon = this.getSvgIcon();
  }

  private hasCompoundFilterSupport(filter: Filter): boolean {
    return this.getCompoundFilterConfig(filter.key as number) !== null || (filter.compoundFilters?.length ?? 0) > 0;
  }

  private getCompoundFilterConfig(filterKey: number): CompoundFilterConfig | null {
    return this.filterConfig?.compoundFilters?.[filterKey] ?? null;
  }

  /**
   * Get the svg icon to show in the filterOptionMenu
   */
  getSvgIcon(): string {
    const isNonDefault = [INCLUDE_EMPTY.value, EXCLUDE_EMPTY.value, EXCLUDE_SELECTION.value].includes(
      this.filterOptionControl.value
    );
    return isNonDefault || this.isCompoundSelectionNonDefault()
      ? this.optionMenuIcons.selected
      : this.optionMenuIcons.default;
  }

  private isCompoundSelectionNonDefault(): boolean {
    if (!this.compoundFilterConfig()) return false;
    const defaults = this.defaultSelectedKeys();
    const current = this.pendingCompoundFilterKeys ?? this.currentCompoundFilterKeys() ?? defaults;
    if (current.length !== defaults.length) return true;
    return !current.every(k => defaults.includes(k));
  }

  setDateRangeFilter(dateOption: DateRangeFilterOption): void {
    const parentGroup = this.parentGroupControl as UntypedFormGroup;
    const fromCtrl = parentGroup?.controls?.['from'] as UntypedFormControl;
    const toCtrl = parentGroup?.controls?.['to'] as UntypedFormControl;
    if (fromCtrl && toCtrl) {
      const { from, to } = getDatesBySelectedOption(dateOption.key as DateRangeFilterOptions, this.isReports());
      fromCtrl.setValue(from);
      toCtrl.setValue(to);
      // Used in the landing page to save the selected period by the user
      const enumCtrl = parentGroup?.controls?.['enumSelection'] as UntypedFormControl;
      if (enumCtrl) {
        enumCtrl.setValue(dateOption.key);
      }
    }
  }

  get parentGroupControl(): UntypedFormGroup | UntypedFormControl {
    const form = this.form();
    const filter = this.filter();
    return (form?.controls?.[filter.key] || form?.controls?.[filter.field]) as UntypedFormGroup | UntypedFormControl;
  }

  onMenuClosed(): void {
    if (!this.hasCompoundFilters() || this.pendingCompoundFilterKeys === undefined) return;
    this.currentCompoundFilterKeys.set([...this.pendingCompoundFilterKeys]);
    this.pendingCompoundFilterKeys = undefined;
    this.filterOptionChange.emit(this.buildUpdatedFilter());
    this.optionMenuIcon = this.getSvgIcon();
  }

  private buildUpdatedFilter(): Filter {
    const filter = this.filter();
    const compoundFilters = this.buildAllCompoundDefs(filter);
    return {
      ...filter,
      ...(this.excludeEmpty ? { values: null } : {}),
      includeEmpty: this.includeEmpty,
      excludeEmpty: this.excludeEmpty,
      excludeSelection: this.excludeSelection,
      ...(compoundFilters !== undefined ? { compoundFilters } : {}),
    };
  }

  /**
   * Builds the full compound-filter definitions to emit.
   * ALL available options are always included so they survive component recreation:
   *  - selected keys  → { ...def, values: mainValues }  (active in query)
   *  - unselected keys → { ...def, values: null }       (preserves labels; API treats null as inactive)
   */
  private buildAllCompoundDefs(filter: Filter): IOption[] | undefined {
    const keys = this.currentCompoundFilterKeys();
    if (!keys) return undefined;

    const parentKey = filter.key as number;
    const selectedSet = new Set(keys);
    const mainValues = filter.values ?? [];

    // filter.compoundFilters carries labels from the server — preserve them.
    // Registry fills in any keys not already in filter.compoundFilters.
    const fromFilter: IOption[] = filter.compoundFilters ?? [];

    const subDefs = [...fromFilter]
      .filter(d => (d.key as number) !== parentKey)
      .map(def => ({
        ...def,
        values: selectedSet.has(def.key as number) ? mainValues : null,
      }));

    return subDefs.length > 0 ? subDefs : undefined;
  }

  private disableFormOnExcludeEmpty(): void {
    const ctrl = this.parentGroupControl;
    if (!ctrl) return;
    if (this.excludeEmpty) {
      this.disableDateFilter = true;
      const defaultValue = this.filter().type === 'range' ? { from: null, to: null } : null;
      ctrl.reset(defaultValue, { emitEvent: false });
      ctrl.disable({ emitEvent: false });
    } else {
      this.disableDateFilter = false;
      ctrl.enable({ onlySelf: true, emitEvent: false });
    }
  }

  private setFilterOptionControl(): void {
    const filter = this.filter() as Filter & { includeEmpty?: boolean; excludeEmpty?: boolean };

    this.includeEmpty = !!filter.includeEmpty;
    this.excludeEmpty = !!filter.excludeEmpty;
    this.excludeSelection = !!filter.excludeSelection;

    const selectedOption = this.includeEmpty
      ? INCLUDE_EMPTY.value
      : this.excludeEmpty
        ? EXCLUDE_EMPTY.value
        : this.excludeSelection
          ? EXCLUDE_SELECTION.value
          : DEFAULT_OPT.value;

    this.filterOptionControl.setValue(selectedOption);
    this.filterOptionControl.valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      this.includeEmpty = value === INCLUDE_EMPTY.value;
      this.excludeEmpty = value === EXCLUDE_EMPTY.value;
      this.excludeSelection = value === EXCLUDE_SELECTION.value;
      if (this.pendingCompoundFilterKeys !== undefined) {
        this.currentCompoundFilterKeys.set([...this.pendingCompoundFilterKeys]);
        this.pendingCompoundFilterKeys = undefined;
      }
      this.disableFormOnExcludeEmpty();
      this.filterOptionChange.emit(this.buildUpdatedFilter());
      this.optionMenuIcon = this.getSvgIcon();
    });
    this.optionMenuIcon = this.getSvgIcon();
  }

  private initCompoundFilterState(): void {
    if (!this.hasCompoundFilters()) return;

    const filter = this.filter();
    const registeredConfig = this.getCompoundFilterConfig(filter.key as number);
    if (filter.compoundFilters?.length) {
      // Generate groups could happend that the configuration only has the registered groups
      // Keep the order from the BE for the compoundFilters
      const generatedGroups = filter.compoundFilters.reduce(
        (acc: Record<string, any>, cf: Record<string, any>, index) => {
          const customConfig = registeredConfig?.groups?.find(g => g.items.some(i => i.key === cf.key));
          if (customConfig && customConfig.label) {
            if (acc[customConfig.label] == null) {
              acc[customConfig.label] = { label: customConfig.label, items: [], index };
            }
            acc[customConfig.label].items.push({ key: cf.key, label: cf.value ?? '', index });
          } else {
            // Generate its own group
            acc[cf.field] = { label: cf.value, items: [{ key: cf.key, label: cf.value, index }], index };
          }
          return acc;
        },
        {}
      );
      this.compoundFilterConfig.set({
        defaultSelectedKeys: registeredConfig?.defaultSelectedKeys ?? [],
        groups: Object.values(generatedGroups).sort((a, b) => a.index - b.index),
      });
    } else {
      this.compoundFilterConfig.set(null);
    }

    this.pendingCompoundFilterKeys = undefined;

    // Derive all available keys from the built config.
    const allAvailableKeys = (this.compoundFilterConfig()?.groups ?? []).flatMap(g => g.items.map(i => i.key));

    // Only entries with non-null values are considered "active" (selected by the user).
    // compoundFilters entries carry a runtime `values` field (not declared on IOption type).
    const allSubDefs: IOption[] = filter.compoundFilters ?? [];
    const selectedSubDefs = allSubDefs.filter(cf => (cf as any).values != null);

    // Default to ALL available keys when there is no prior selection.
    this.currentCompoundFilterKeys.set(
      selectedSubDefs.length
        ? [filter.key as number, ...selectedSubDefs.map(cf => cf.key as number)]
        : allAvailableKeys.length
          ? allAvailableKeys
          : undefined
    );

    this.optionMenuIcon = this.getSvgIcon();
  }
}
